import { ReactNode } from 'react';

import VariantPill from '../common/VariantPill';
import { useGetWorkflowFilterOptionsQuery } from 'src/services/insights';
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter';
import { CommonIntentWorkflowType } from 'src/utils/enums';

interface useGetWorkflowFilterOptionsProps {
  end: number;
  start: number;
}

export interface WorkflowFilterOption {
  label: string;
  optionEndAdornment?: ReactNode;
  type?: string;
  value: string;
}

export default function useGetWorkflowFilterOptions({
  end,
  start,
}: useGetWorkflowFilterOptionsProps) {
  const { data } = useGetWorkflowFilterOptionsQuery({ end, start });
  const workflowOptions = data?.data ?? [];

  // Helper function to count label occurrences
  const getLabelCounts = (options: typeof workflowOptions) => {
    return options.reduce(
      (acc, workflow) => {
        acc[workflow.label] = (acc[workflow.label] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  };

  /**
   * Helper function to format labels
   * If count > 1 - meaning there are multiple workflows with the same label (e.g. "Standard Handoff"),
   * append the type to the label to differentiate them.
   * (e.g. "Standard Handoff - API")
   * (e.g. "Standard Handoff - Widget")
   * If still not unique (due to possible workflow dupe issues - show id as well)
   */
  const formatLabel = (
    label: string,
    type: string,
    count: number,
    id?: string,
    isUnique?: boolean,
  ) => {
    if (count > 1) {
      const formattedType =
        type === 'api' ? type.toUpperCase() : capitalizeFirstLetter(type);
      const formattedLabel = `${label} - ${formattedType}`;
      return !isUnique && id ? `${formattedLabel} (${id})` : formattedLabel;
    }
    return label;
  };

  const labelCounts = getLabelCounts(workflowOptions);

  const options: WorkflowFilterOption[] = [];

  [...workflowOptions]
    .sort((a, b) => a.label.localeCompare(b.label))
    .forEach(workflow => {
      // For dupe workflow cases
      const isUnique =
        workflowOptions.filter(
          w => w.label === workflow.label && w.type === workflow.type,
        ).length === 1;

      const baseLabel = formatLabel(
        workflow.label,
        workflow.type ?? '',
        labelCounts[workflow.label],
        workflow.value,
        isUnique,
      );

      if (workflow.ab_test_variants?.length) {
        // Emit variant rows instead of single workflow row
        workflow.ab_test_variants.forEach(variant => {
          options.push({
            label: baseLabel,
            optionEndAdornment: <VariantPill variant={variant} />,
            type: workflow.type,
            value: `workflow_versions.${workflow.value}:${variant.intent_workflow_version}`,
          });
        });
      } else {
        // Regular workflow without AB test
        options.push({
          label: baseLabel,
          type: workflow.type,
          value: `workflow_id.${workflow.value}`,
        });
      }
    });

  const fallbackWorkflowIds = workflowOptions
    .filter(
      workflow =>
        workflow.secondary_value === CommonIntentWorkflowType.FALLBACK,
    )
    .map(workflow => workflow.value);

  return { fallbackWorkflowIds, options };
}
